import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { TagSummary } from "@media-viewer/shared";

interface BatchTagPickerProps {
  selected: TagSummary[];
  onChange: (next: TagSummary[]) => void;
  /**
   * Heading line above the picker. `undefined` keeps the default upload copy;
   * pass `null` to render no heading (e.g. inside a modal that has its own title).
   */
  heading?: string | null;
}

/**
 * Session-level tag selector for the upload page. Unlike TagPicker (which
 * mutates a single media row), this just maintains a list of tags in the
 * parent's state. New tags are created server-side immediately (idempotent
 * on slug) so we always hold real tag IDs to pass to finalize.
 */
export function BatchTagPicker({ selected, onChange, heading }: BatchTagPickerProps) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const allTags = useQuery({ queryKey: ["tags"], queryFn: api.tags });

  const selectedIds = new Set(selected.map((t) => t.id));

  function add(tag: TagSummary) {
    if (selectedIds.has(tag.id)) return;
    onChange([...selected, tag]);
    setInput("");
  }
  function remove(tagId: number) {
    onChange(selected.filter((t) => t.id !== tagId));
  }

  const createAndAdd = useMutation({
    mutationFn: (name: string) => api.createTag(name),
    onSuccess: (tag) => {
      add(tag);
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const trimmed = input.trim().toLowerCase();
  const available = (allTags.data ?? [])
    .filter((t) => !selectedIds.has(t.id))
    .filter((t) => (trimmed ? t.name.toLowerCase().includes(trimmed) : true))
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name));

  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/50 p-3">
      {heading !== null && (
        <div className="mb-2 text-sm text-zinc-300">
          {heading ?? (
            <>
              Tag this upload session
              <span className="ml-2 text-xs text-zinc-500">
                applied to every file uploaded below
              </span>
            </>
          )}
        </div>
      )}
      <div className="mb-2 flex flex-wrap gap-1">
        {selected.length === 0 && <span className="text-xs text-zinc-500">No tags selected.</span>}
        {selected.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 rounded bg-zinc-700 px-2 py-0.5 text-xs"
          >
            {t.name}
            <button
              onClick={() => remove(t.id)}
              className="text-zinc-400 hover:text-zinc-100"
              aria-label={`Remove ${t.name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && input.trim().length > 0) {
            const exact = (allTags.data ?? []).find(
              (t) => t.name.toLowerCase() === input.trim().toLowerCase(),
            );
            if (exact) add(exact);
            else createAndAdd.mutate(input.trim());
          }
        }}
        placeholder="Type to filter or add new (e.g. vacation summer 2026)…"
        className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-sm focus:border-zinc-500 focus:outline-none"
      />
      {available.length > 0 && (
        <div className="mt-2 flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded border border-zinc-800 bg-zinc-900/40 p-1">
          {available.map((t) => (
            <button
              key={t.id}
              onClick={() => add(t)}
              className="rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300 hover:border-emerald-600 hover:bg-zinc-700"
              title={t.count != null ? `Applied to ${t.count} item${t.count === 1 ? "" : "s"}` : undefined}
            >
              + {t.name}
              {t.count != null && t.count > 0 && <span className="ml-1 text-zinc-500">{t.count}</span>}
            </button>
          ))}
        </div>
      )}
      {trimmed && available.length === 0 && (
        <div className="mt-2 text-xs text-zinc-500">
          No existing tag matches "{input.trim()}". Press Enter to create it.
        </div>
      )}
    </div>
  );
}
